import { Button, Modal, Spinner } from '@sjuni/ui';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { apiFetch } from '@/lib/api/client';
import { useEditorLocale } from '@/lib/editorLocale/useEditorLocale';
import { valueFor } from '@/lib/forms/translatable';

import type { DocumentReference, DocumentRow } from './types';

/**
 * docs/05-ADMIN-DASHBOARD.md §5.4 — "Warning before deleting a document that is
 * referenced by an announcement, modal, page block, or menu item."
 *
 * Not a generic confirmation with an extra sentence. The references are fetched
 * when the dialog opens and listed by name, because the editor's real question
 * is "what breaks if I do this", and a count does not answer it — three of the
 * four references are nullable foreign keys, so deleting empties a download
 * button on a page nobody thinks to check, and nothing anywhere says why.
 *
 * The fetch is here rather than on the list row: finding a reference means
 * scanning every page's block JSON, which is far too much work to do for every
 * row of a list that mostly gets read.
 */
export interface DocumentDeleteDialogProps {
  document: DocumentRow;
  busy?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export function DocumentDeleteDialog({
  document,
  busy = false,
  onCancel,
  onConfirm,
}: DocumentDeleteDialogProps) {
  const { t } = useTranslation();
  const { locale } = useEditorLocale();

  const references = useQuery({
    queryKey: ['admin', 'documents', 'references', document.id],
    queryFn: () =>
      apiFetch<{ data: DocumentReference[] }>(`/admin/documents/${document.id}/references`),
  });

  const found = references.data?.data ?? [];

  return (
    <Modal
      open
      onClose={onCancel}
      title={t('documents.delete.title')}
      closeLabel={t('common.close')}
      className="sm:w-[min(92vw,560px)]"
    >
      <div className="space-y-4">
        <p className="text-body-sm text-secondary">
          {t('documents.delete.body', { title: valueFor(document.title, locale) })}
        </p>

        {references.isPending ? (
          <p className="flex items-center gap-2 text-caption text-muted">
            <Spinner size="sm" label={t('documents.delete.checking')} />
            {t('documents.delete.checking')}
          </p>
        ) : null}

        {found.length > 0 ? (
          <div
            // `alert`, unlike the slug field's redirect notice: this one is a
            // consequence the editor has not been told about yet, and it
            // appears at the moment they are deciding.
            role="alert"
            className="space-y-2 rounded-card border-s-4 border-danger bg-surface-alt p-3"
          >
            <p className="flex items-center gap-2 text-body-sm font-semibold text-primary">
              <AlertTriangle size={14} strokeWidth={1.75} aria-hidden="true" />
              {t('documents.delete.referenced', { count: found.length })}
            </p>

            <ul className="space-y-0.5 text-caption text-secondary">
              {found.map((reference) => (
                <li key={`${reference.type}-${reference.id}`}>
                  {/*
                    The type name, with the raw type as the fallback — an
                    interpolated key is invisible to the translation test, and a
                    new reference kind arriving before its label should read as
                    `page_section` rather than as `documents.references.…`.
                  */}
                  <span className="font-semibold">
                    {t(`documents.references.${reference.type}`, reference.type)}
                  </span>
                  {' — '}
                  {reference.label || t('documents.references.untitled')}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {/* Soft, so it can come back — CLAUDE.md rule 11, and §6.5 asks for
            restore to be obvious afterwards. */}
        <p className="text-caption text-muted">{t('confirm.reversible')}</p>

        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onCancel} disabled={busy}>
            {t('common.cancel')}
          </Button>

          <Button
            type="button"
            variant="danger"
            onClick={onConfirm}
            /*
             * Enabled straight away, and never blocked by what the check finds.
             *
             * §5.4 asks for a warning, not a veto — withdrawing a document a
             * page still links to is a real thing a university does, and the
             * editor is the one who knows. Waiting on the check would be a veto
             * with a timer on it: the scan reads every page's block JSON, so
             * under load it is slow enough to feel like the button is broken.
             */
            disabled={busy}
            aria-busy={busy || undefined}
          >
            {t('common.delete')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
